import { Link } from 'react-router-dom'

const guide = {
  slug: 'porn-blocker-with-a-delay',
  category: 'holds',
  kicker: 'Design · The 24-hour rule',
  title: 'Why a 24-hour delay beats a password: the design behind a blocker that holds',
  seoTitle: 'Porn Blocker With a Delay: Why 24 Hours Beats a Password',
  description: `An urge is short; a password you know is instant. Why a one-way 24-hour delay is what makes a self-imposed block hold, and how the delay is enforced.`,
  published: '2026-09-03',
  minutes: 6,
  intro: `Every blocker asks the same thing at the moment you want to turn it
          off: a password, a code, a confirmation. And every person who set
          the thing up has the answer. This guide is about a different design:
          instead of asking who you are, the block asks when you are. Tighten
          now, loosen tomorrow. It sounds small. It is the whole difference
          between a lock and a note.`,
  next: ['porn-blocker-you-cant-uninstall', 'how-to-quit-porn', 'porn-blocker-for-adults'],
  body: (
    <>
      <h2>An urge is a short event</h2>
      <p>
        Whatever the exact figure for you, an urge is measured in minutes,
        not hours. It builds, it peaks, it passes — if nothing feeds it. The
        relapse is what happens when the door is closer than the end of the
        urge. A password you know is a door that opens in five seconds. That
        is why blockers with passwords, codes and "are you sure" screens
        don't hold: the answer is always available faster than the urge
        fades.
      </p>

      <h2>A delay changes the question</h2>
      <p>
        A blocker with a delay doesn't ask <em>who</em> you are. It asks
        <em> when</em> you are. You can request anything you like — remove a
        locked day, shorten a curfew, release a device — and the request is
        granted, twenty-four hours later. There is no one to convince and
        nothing to guess. The you of 2am makes the request; the you of
        tomorrow afternoon, rested, receives it and, almost always, cancels
        it. The mechanism doesn't fight the urge. It outlasts it.
      </p>

      <h2>The delay only runs one way</h2>
      <p>
        This is the part most "delay" features miss. If both tightening and
        loosening wait a day, you'll never tighten on a good day, because
        good days don't feel urgent. So in SELFCNTRL:
      </p>
      <ul>
        <li><b>Tightening is immediate.</b> Add a locked day, extend the curfew, block another category, add a specific site — done, now.</li>
        <li><b>Loosening waits twenty-four hours.</b> Remove a day, shorten a curfew, release a device — tomorrow.</li>
        <li><b>The floor never drops.</b> The number of locked days you set at the start is the minimum for good. You can only go up from it.</li>
      </ul>
      <p>
        Over weeks this ratchet does something no password does: every good
        day can raise the bar, and no bad night can lower it.
      </p>

      <h2>Where the delay is enforced</h2>
      <p>
        A delay is only as good as what enforces it. If the block is an app
        you can delete, the delay is decoration. That's why the delay lives
        in your account, and the filter lives somewhere you can't reach on
        impulse:
      </p>
      <ul>
        <li>On <b>iPhone and iPad</b>, a supervised configuration profile that can't be removed from the device and forbids adding a VPN. There's nothing on the phone to bypass; there's only the request, and the request waits. <Link to="/guides/supervised-iphone-explained">Supervision explained</Link>.</li>
        <li>On <b>Mac and Windows</b>, the filter installs as an app tied to your account. You're the administrator of your own computer, so honesty requires saying it: a determined person can fight their own machine. What the delay does there is make every loosening a decision made in daylight.</li>
      </ul>

      <h2>The straight cut and the taper</h2>
      <p>
        The same design produces the two paces you choose from at the start:
      </p>
      <ul>
        <li><b>Straight cut.</b> Seven days a week locked from the first minute. Hardest first five days, shortest path.</li>
        <li><b>Taper.</b> Four locked days a week to begin with, then one more every two weeks until seven out of seven. You never have to decide to add a day; the schedule does it, and the delay rule means you can't take one back.</li>
      </ul>
      <p>
        Either way, night curfews can cut the internet entirely in the hours
        where relapses actually happen, say 11pm to 7am, per weekday, to the
        minute. Tightening a curfew: now. Loosening: tomorrow.
      </p>

      <h2>Delay vs the alternatives</h2>
      <table>
        <thead>
          <tr><th>Mechanism</th><th>What it asks at 2am</th><th>Do you have the answer?</th><th>Holds?</th></tr>
        </thead>
        <tbody>
          <tr><td>Password / passcode</td><td>Who are you?</td><td>Yes</td><td>No</td></tr>
          <tr><td>Someone else's passcode</td><td>Can you reach them?</td><td>Often, or via Apple ID reset</td><td>Sometimes</td></tr>
          <tr><td>"Are you sure?" screens</td><td>Are you sure?</td><td>Yes</td><td>No</td></tr>
          <tr><td>Accountability report</td><td>Will you accept the shame?</td><td>At 2am, yes</td><td>Sometimes</td></tr>
          <tr><td>24-hour delay, non-removable profile</td><td>When are you?</td><td>Not until tomorrow</td><td>Yes</td></tr>
        </tbody>
      </table>

      <h2>Getting paid to wait</h2>
      <p>
        The other half of the design is that holding pays. Every month the
        block stays on, CNTRL points land in your account, and 1,000 CNTRL
        buy a $5 gift card. It's not why anyone quits; it's a small reason
        to leave the request uncancelled tomorrow afternoon. Details on the
        <Link to="/"> home page</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Why 24 hours and not longer?',
      a: `Long enough to outlast any urge and to include a night's sleep;
          short enough that the block stays livable and honest. You remain
          free to stop, deliberately. A delay of weeks would turn a tool
          into a trap.`
    },
    {
      q: 'Can I make the delay shorter?',
      a: `No. A delay you could shorten would just be a password with extra
          steps. Tightening is always immediate; loosening always waits a
          day.`
    },
    {
      q: 'What if there is an emergency and I need a site unblocked now?',
      a: `Filtering is by category, so ordinary sites are never blocked.
          If something legitimate is misclassified, you can report it from
          your account; unblocking a category or a device is the thing that
          waits, and it waits for a reason.`
    },
    {
      q: 'Does the delay apply to releasing a device entirely?',
      a: `Yes. Releasing a device from your account takes effect after the
          cooling-off delay, then the profile is removed. Slow on purpose,
          never impossible.`
    }
  ]
}

export default guide
